var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getPaginatedApiParams } from 'lib-shared-api-utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { appSearchStringToParams } from 'utils/url/appSearchParams';
/** The merge strategies and their merge functions. */
const mergeStrategyFn = {
    prepend: (items, next) => [...next, ...items],
    append: (items, next) => [...items, ...next],
    replace: (_items, next) => next,
};
/**
 * Represents a hook that simplifies working with paginated endpoints. It
 * encapsulates the items, while exposes functions for navigating the pages,
 * changing the arg, re-fetching, updating an item, and more.
 */
export const useLazyPaginatedEndpoint = ({ endpoint, infinite = false, itemsSelector, totalSelector, }) => {
    const dispatch = useAppDispatch();
    // Data.
    const [items, setItems] = useState([]);
    const [asyncStatus, setAsyncStatus] = useState('uninitialized');
    // Pagination.
    const [total, setTotal] = useState();
    const [nextPagePath, setNextPagePath] = useState(null);
    const [previousPagePath, setPreviousPagePath] = useState(null);
    const [isPaginating, setIsPaginating] = useState(false);
    // State.
    const timeoutRef = useRef();
    const skipFetchRef = useRef(false);
    const debounceTimeRef = useRef(0);
    const [arg, setArg] = useState();
    const [debouncedArg, setDebouncedArg] = useState();
    /** Change the arg, which in effect fetches the items anew. */
    const changeArg = useCallback((newArg, { skipFetch = false, debounceTime = 0 } = {}) => {
        skipFetchRef.current = skipFetch;
        debounceTimeRef.current = debounceTime;
        setArg((prevArg) => (Object.assign(Object.assign({}, (prevArg !== null && prevArg !== void 0 ? prevArg : {})), newArg)));
    }, []);
    /** Gets the items. */
    const getItems = useCallback(({ page, mergeStrategy = 'replace' } = {}) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        // We batch the state updates as React would otherwise schedule them
        // one-by-one when set in an async function - i.e. without this the
        // consumer would see a pending async status with pagination false and
        // then in next cycle see pagination true when in fact these two values
        // should be updated together. https://github.com/reactwg/react-18/discussions/21
        unstable_batchedUpdates(() => {
            setAsyncStatus('pending');
            if (page)
                setIsPaginating(true);
        });
        const result = dispatch(endpoint.initiate((_a = page) !== null && _a !== void 0 ? _a : arg, {
            forceRefetch: true,
        }));
        result.unsubscribe();
        const { data } = yield result;
        if (data) {
            unstable_batchedUpdates(() => {
                setItems(mergeStrategyFn[mergeStrategy](items, itemsSelector(data)));
                setAsyncStatus('fulfilled');
                setIsPaginating(false);
                setNextPagePath(data.meta.next_page);
                setPreviousPagePath(data.meta.prev_page);
                if (totalSelector) {
                    setTotal(totalSelector(data));
                }
            });
            if (page) {
                const search = page.substring(page.indexOf('?') + 1);
                const params = appSearchStringToParams(search);
                const paginatedApiParams = getPaginatedApiParams(params);
                delete params['page'];
                delete params['page_size'];
                delete params['pageSize'];
                delete params['page_number'];
                delete params['count'];
                changeArg(Object.assign(Object.assign({}, params), paginatedApiParams), { skipFetch: true });
            }
        }
        else {
            unstable_batchedUpdates(() => {
                setAsyncStatus('rejected');
                if (page)
                    setIsPaginating(false);
            });
        }
    }), [arg, endpoint, items, changeArg, dispatch, itemsSelector, totalSelector]);
    /** Get the items for the next page. */
    const nextPage = useCallback(() => {
        if (!nextPagePath) {
            throw Error('Next page does not exist');
        }
        getItems({
            page: nextPagePath,
            mergeStrategy: infinite ? 'append' : 'replace',
        });
    }, [getItems, infinite, nextPagePath]);
    /** Get the items for the previous page. */
    const previousPage = useCallback(() => {
        if (!previousPagePath) {
            throw Error('Previous page does not exist');
        }
        getItems({
            page: previousPagePath,
            mergeStrategy: infinite ? 'prepend' : 'replace',
        });
    }, [getItems, infinite, previousPagePath]);
    /** Updates an item. */
    const updateItem = useCallback((itemToUpdate, compareFn) => {
        setItems((items) => items.map((item) => compareFn(item, itemToUpdate) ? itemToUpdate : item));
    }, []);
    /** Removes an item. */
    const removeItem = useCallback((itemToRemove, compareFn) => {
        setItems((items) => items.filter((item) => !compareFn(item, itemToRemove)));
    }, []);
    /** Re-fetches the items anew. */
    const refetchItems = useCallback(() => {
        return getItems({
            mergeStrategy: 'replace',
        });
    }, [getItems]);
    /** Searches the items, which in effect fetches the items anew. */
    const search = useCallback((searchTerm, options = { debounceTime: 300 }) => {
        changeArg({
            searchTerm,
            offset: undefined,
            limit: undefined,
        }, options);
    }, [changeArg]);
    useEffect(() => {
        if (arg) {
            if (skipFetchRef.current) {
                // Temporary fix to make sure arg is correctly propagated to the consumer.
                // TODO: [TEAMS-13085] Improve useLazyPaginatedEndpoint consumption
                setDebouncedArg(arg);
                return;
            }
            if (timeoutRef.current)
                clearTimeout(timeoutRef.current);
            // When params are changed, the list needs to be replaced with the new
            // results, hence the replace merge strategy.
            const update = () => {
                getItems({ mergeStrategy: 'replace' });
                setDebouncedArg(arg);
            };
            if (debounceTimeRef.current > 0) {
                timeoutRef.current = window.setTimeout(update, debounceTimeRef.current);
            }
            else {
                update();
            }
        }
        // Excluding `getItems` from the dependencies array to prevent an infinite loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [arg]);
    return {
        items,
        total,
        asyncStatus,
        arg: debouncedArg,
        isPaginating,
        hasPreviousPage: previousPagePath !== null,
        hasNextPage: nextPagePath !== null,
        previousPage,
        nextPage,
        changeArg,
        search,
        updateItem,
        removeItem,
        refetchItems,
    };
};
